import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, MessageCircle, Search, Pencil, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookingWizard } from "@/components/BookingWizard";
import { api, errorText, waLink, toISO } from "@/lib/api";
import { field, Panel } from "@/components/admin/shared";
import { EditAppointmentDialog } from "@/components/admin/EditAppointmentDialog";

const STATUS = { pendiente: "bg-yellow-100 text-yellow-800", confirmada: "bg-emerald-100 text-emerald-800", completada: "bg-sky-100 text-sky-800", cancelada: "bg-red-100 text-red-700" };
const SOURCE = { web: "Sitio", chatbot: "Asistente", admin: "Panel" };

const Filters = ({ f, setF }) => (
  <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    <Select value={f.status || "todas"} onValueChange={(v) => setF({ ...f, status: v === "todas" ? "" : v })}>
      <SelectTrigger className="bg-surface border-line" data-testid="appt-filter-status"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="todas">Todos los estados</SelectItem>
        {Object.keys(STATUS).map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
      </SelectContent>
    </Select>
    <input type="date" aria-label="Desde" className={field} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="appt-filter-from" />
    <input type="date" aria-label="Hasta" className={field} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="appt-filter-to" />
    <div className="relative">
      <Search size={15} className="absolute left-3 top-3 text-mute2" />
      <input placeholder="Buscar nombre, tel. o vehículo" aria-label="Buscar" className={`${field} pl-9`} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="appt-filter-search" />
    </div>
  </div>
);

const Row = ({ a, onChange, onDelete, onEdit, onConfirm }) => (
  <tr className="border-t border-line align-top" data-testid={`appt-row-${a.id}`}>
    <td className="py-3 pr-4 font-mono text-sm whitespace-nowrap">{a.date}<br /><span className="text-brand">{a.time}</span></td>
    <td className="py-3 pr-4 text-sm"><p className="font-semibold">{a.name}</p><p className="text-mute">+{a.phone}</p>{a.whatsapp_opt_in && <p className="text-[11px] text-emerald-400">Acepta WhatsApp</p>}</td>
    <td className="py-3 pr-4 text-sm"><p>{a.service}</p><p className="text-mute">{a.vehicle}{a.plate && ` · ${a.plate}`}</p>{a.notes && <p className="mt-1 text-xs text-mute2">{a.notes}</p>}</td>
    <td className="py-3 pr-4 text-xs text-mute">{SOURCE[a.source] || a.source}</td>
    <td className="py-3 pr-4">
      <select value={a.status} onChange={(e) => onChange(a.id, { status: e.target.value })} aria-label="Estado"
        className={`rounded-sm border-0 px-2 py-1 text-xs font-semibold capitalize ${STATUS[a.status]}`} data-testid={`appt-status-${a.id}`}>
        {Object.keys(STATUS).map((s) => <option key={s} value={s} className="bg-paper text-ink">{s}</option>)}
      </select>
    </td>
    <td className="py-3 whitespace-nowrap">
      {a.status === "pendiente" && <button onClick={() => onConfirm(a)} className="mr-1 inline-flex items-center gap-1 rounded-lg bg-[#25D366] px-2.5 py-1.5 text-xs font-bold text-white" data-testid={`appt-confirm-${a.id}`}><CheckCircle2 size={14} /> Confirmar por WhatsApp</button>}
      <button onClick={() => onEdit(a)} aria-label="Modificar o reprogramar" className="inline-grid h-8 w-8 place-items-center text-green hover:bg-surface rounded-sm" data-testid={`appt-edit-${a.id}`}><Pencil size={16} /></button>
      <a href={waLink(a.phone, `Hola ${a.name}, te escribimos del Taller La Gringa sobre tu cita del ${a.date} a las ${a.time}.`)} target="_blank" rel="noopener noreferrer"
        aria-label="Escribir por WhatsApp" className="inline-grid h-8 w-8 place-items-center text-[#25D366] hover:bg-white/5 rounded-sm" data-testid={`appt-wa-${a.id}`}><MessageCircle size={16} /></a>
      <button onClick={() => onDelete(a.id)} aria-label="Eliminar" className="inline-grid h-8 w-8 place-items-center text-red-400 hover:bg-white/5 rounded-sm" data-testid={`appt-delete-${a.id}`}><Trash2 size={16} /></button>
    </td>
  </tr>
);

export const AppointmentsTab = () => {
  const [f, setF] = useState({ status: "", date_from: toISO(new Date()), date_to: "", q: "" });
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const confirm = async (a) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await api.post(`/admin/appointments/${a.id}/confirm`);
      if (data.sent) { w?.close(); toast.success("Cita confirmada y aviso enviado por WhatsApp Business"); }
      else { if (w) w.location.href = waLink(a.phone, data.message); toast.success("Cita confirmada"); }
      load();
    } catch (e) { w?.close(); toast.error(errorText(e)); }
  };
  const load = useCallback(() => {
    const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
    api.get(`/admin/appointments?${p}`).then((r) => setList(r.data)).catch((e) => toast.error(errorText(e)));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const change = async (id, patch) => {
    try { await api.patch(`/admin/appointments/${id}`, patch); toast.success("Cita actualizada"); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cita definitivamente?")) return;
    await api.delete(`/admin/appointments/${id}`); load();
  };
  return (
    <Panel title="Citas" action={<button className="btn-gold !py-2" onClick={() => setOpen(true)} data-testid="admin-new-appt-btn"><Plus size={16} /> Nueva cita</button>}>
      <Filters f={f} setF={setF} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-left">
          <thead><tr className="text-xs uppercase tracking-wider text-mute2 [&>th]:pb-2"><th>Fecha</th><th>Cliente</th><th>Servicio / vehículo</th><th>Origen</th><th>Estado</th><th /></tr></thead>
          <tbody>{list.map((a) => <Row key={a.id} a={a} onChange={change} onDelete={remove} onEdit={setEditing} onConfirm={confirm} />)}</tbody>
        </table>
        {!list.length && <p className="py-8 text-center text-mute" data-testid="appt-empty">No hay citas con estos filtros.</p>}
      </div>
      <EditAppointmentDialog appt={editing} onClose={() => setEditing(null)} onSaved={load} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-paper border-line">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Registrar cita manual</DialogTitle></DialogHeader>
          <BookingWizard admin onDone={() => { setOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
    </Panel>
  );
};
